import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, Search, Filter, MoreVertical, Mail, Phone, Home, Trash2, CheckCircle2, Clock } from 'lucide-react';
import { useLeadStore, Lead } from '../store/useLeadStore';
import { useScreenInit } from '../useScreenInit';
export function AdminDashboard() {
  useScreenInit();
  const {
    leads,
    updateLeadStatus,
    deleteLead
  } = useLeadStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<Lead['status'] | 'all'>('all');
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) || lead.address.toLowerCase().includes(searchTerm.toLowerCase()) || lead.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const getStatusColor = (status: Lead['status']) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'contacted':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'qualified':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'archived':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-303">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-304">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-305">
          <div className="flex justify-between items-center h-16" data-id="element-306">
            <div className="flex items-center" data-id="element-307">
              <Link to="/" className="mr-4 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-308">
                <ArrowLeft className="w-5 h-5" data-id="element-309" />
              </Link>
              <div data-id="element-310">
                <h1 className="text-xl font-serif font-semibold text-gray-900" data-id="element-311">
                  Lead Management
                </h1>
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-312">
                  Kareem Jamal • 91311
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4" data-id="element-313">
              <div className="text-sm text-gray-600" data-id="element-314">
                Total Leads:{' '}
                <span className="font-semibold text-gray-900" data-id="element-315">
                  {leads.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-316">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0" data-id="element-317">
          <div className="relative w-full sm:w-96" data-id="element-318">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-319">
              <Search className="h-4 w-4 text-gray-400" data-id="element-320" />
            </div>
            <input type="text" placeholder="Search leads by name, address, or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-brand-gold focus:border-brand-gold" data-id="element-321" />
          </div>

          <div className="flex items-center space-x-2 w-full sm:w-auto" data-id="element-322">
            <Filter className="h-4 w-4 text-gray-400" data-id="element-323" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="block w-full sm:w-auto pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:ring-brand-gold focus:border-brand-gold" data-id="element-324">
              <option value="all" data-id="element-325">All Statuses</option>
              <option value="new" data-id="element-326">New</option>
              <option value="contacted" data-id="element-327">Contacted</option>
              <option value="qualified" data-id="element-328">Qualified</option>
              <option value="archived" data-id="element-329">Archived</option>
            </select>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden" data-id="element-330">
          <div className="overflow-x-auto" data-id="element-331">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-332">
              <thead className="bg-gray-50" data-id="element-333">
                <tr data-id="element-334">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-335">
                    Lead Details
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-336">
                    Property Address
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-337">
                    Date Received
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-338">
                    Status
                  </th>
                  <th scope="col" className="relative px-6 py-3" data-id="element-339">
                    <span className="sr-only" data-id="element-340">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-341">
                {filteredLeads.length === 0 ? <tr data-id="element-342">
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-343">
                      <div className="flex flex-col items-center justify-center" data-id="element-344">
                        <Home className="w-12 h-12 text-gray-300 mb-4" data-id="element-345" />
                        <p className="text-lg font-medium text-gray-900" data-id="element-346">
                          No leads found
                        </p>
                        <p className="text-sm" data-id="element-347">
                          Try adjusting your search or filters.
                        </p>
                      </div>
                    </td>
                  </tr> : filteredLeads.map(lead => <tr key={lead.id} className="hover:bg-gray-50 transition-colors" data-id="element-348">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-349">
                        <div className="flex items-center" data-id="element-350">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-brand-gold/10 flex items-center justify-center text-brand-gold font-serif font-bold text-lg" data-id="element-351">
                            {lead.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-4" data-id="element-352">
                            <div className="text-sm font-medium text-gray-900" data-id="element-353">
                              {lead.name}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center mt-1" data-id="element-354">
                              <Mail className="w-3 h-3 mr-1" data-id="element-355" /> {lead.email}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center mt-0.5" data-id="element-356">
                              <Phone className="w-3 h-3 mr-1" data-id="element-357" /> {lead.phone}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-358">
                        <div className="text-sm text-gray-900 flex items-start max-w-xs truncate" data-id="element-359">
                          <Home className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-360" />
                          <span className="truncate" title={lead.address} data-id="element-361">
                            {lead.address}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-362">
                        <div className="text-sm text-gray-900 flex items-center" data-id="element-363">
                          <Clock className="w-4 h-4 mr-2 text-gray-400" data-id="element-364" />
                          {format(new Date(lead.date), 'MMM d, yyyy')}
                        </div>
                        <div className="text-xs text-gray-500 ml-6" data-id="element-365">
                          {format(new Date(lead.date), 'h:mm a')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-366">
                        <select value={lead.status} onChange={e => updateLeadStatus(lead.id, e.target.value as Lead['status'])} className={`text-xs font-medium px-2.5 py-1 rounded-full border focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-brand-gold appearance-none cursor-pointer ${getStatusColor(lead.status)}`} data-id="element-367">
                          <option value="new" data-id="element-368">New</option>
                          <option value="contacted" data-id="element-369">Contacted</option>
                          <option value="qualified" data-id="element-370">Qualified</option>
                          <option value="archived" data-id="element-371">Archived</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-372">
                        <button onClick={() => {
                    if (window.confirm('Are you sure you want to delete this lead?')) {
                      deleteLead(lead.id);
                    }
                  }} className="text-gray-400 hover:text-red-600 transition-colors p-2 rounded-full hover:bg-red-50" title="Delete Lead" data-id="element-373">
                          <Trash2 className="w-4 h-4" data-id="element-374" />
                        </button>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>;
}